import {IShopRating, IShopRatingSummary, RatingsFactory} from "@connox/typescript-library";
import React, {useRef, useEffect, useState, Fragment} from "react";
import ReactDOM from "react-dom";
import RatingFilterMobile from "./RatingFilter/RatingFilterMobile";
import RatingFilter from "./RatingFilter/RatingFilter";
import Drawer from "../lib/form/Drawer/Drawer";
import RatingStars from "../lib/ratingStars/RatingStars";
import {useTranslation} from "react-i18next";
import ShopRatingComments from "./ShopRatingComments/ShopRatingComments";
import ShopRatingSummary from "./ShopRatingSummary/ShopRatingSummary";
import {getShopId} from "../lib/getShopId";
import {useMediaQuery} from "react-responsive";
import styles from "./ShopRatingSection.module.scss";

export enum ShowFilter {
  all = "all",
  one = "one",
}

export enum SortFilter {
  newest = "newest",
  best = "best",
  worst = "worst",
}

export type FilterState = {filterSet: Set<number>; show: keyof typeof ShowFilter; sort: keyof typeof SortFilter};

type IShopRatingSectionState = {
  summary?: IShopRatingSummary;
  sum: number;
  allComments: IShopRating[];
  itemComments?: IShopRating[];
  filteredComments: IShopRating[];
  filter: FilterState;
};

export const initialFilterState: FilterState = {
  show: ShowFilter.all,
  sort: SortFilter.newest,
  filterSet: new Set<number>(),
};

const ShopRatingSection: React.FC<{itemIds: number[]; headlineElement: HTMLElement | null}> = ({
  itemIds,
  headlineElement,
}) => {
  const headlineDiv = useRef<HTMLDivElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [state, setState] = useState<IShopRatingSectionState>({
    allComments: [],
    filteredComments: [],
    sum: 0,
    filter: initialFilterState,
  });
  const {t} = useTranslation();
  const isMobile = useMediaQuery({maxWidth: 650});

  useEffect(() => {
    const ratingsButtons = document.querySelectorAll(".ga-go-to-ratings");
    if (ratingsButtons.length) {
      ratingsButtons.forEach((ratingsButton) => {
        ratingsButton.addEventListener("click", handleRatingsClick);
      });
    }
    void getCommentsSum();

    return () => {
      if (ratingsButtons.length) {
        ratingsButtons.forEach((ratingsButton) => {
          ratingsButton.removeEventListener("click", handleRatingsClick);
        });
      }
    };
  }, []);

  useEffect(() => {
    if (state.filter.show === "all") {
      setState({...state, itemComments: undefined});
    } else {
      const itemId = document.querySelector(".product-attributes > table tr > td:nth-of-type(2)")?.innerHTML;
      if (itemId) {
        const commentsForItem = state.allComments.filter((comment) => comment.itemId === Number(itemId));
        setState({...state, itemComments: commentsForItem, filteredComments: commentsForItem});
      }
    }
  }, [state.filter.show]);

  const getCommentsSum = async () => {
    const data = await RatingsFactory.getRatings(getShopId(), {itemIds, offset: 0, limit: 1, summary: true});
    let sum = 0;
    if (data && data.summary) {
      Object.keys(data.summary).forEach((key) => {
        if (data.summary) {
          sum += data.summary[key];
        }
      });
    }
    setState({...state, sum, summary: data.summary});
  };

  const getAllComments = async () => {
    const data = await RatingsFactory.getRatings(getShopId(), {
      itemIds,
      offset: 0,
      limit: state.sum,
    });
    setState({
      ...state,
      allComments: data.ratings,
      filteredComments: data.ratings,
    });
  };

  const handleRatingsClick = () => {
    headlineDiv.current?.parentElement?.classList.add("active");
    headlineDiv.current?.scrollIntoView({behavior: "smooth"});
    if (state.sum > state.allComments.length) {
      void getAllComments();
    }
  };

  const handleToggleClick = (evt: React.MouseEvent<HTMLDivElement>): void => {
    const parent = evt.currentTarget?.parentElement;
    if (parent) {
      parent.classList.toggle("active");
      if (!parent.classList.contains("active")) {
        const accTop = parent.parentElement?.getBoundingClientRect().y;
        if (accTop && accTop < 0) {
          window.scroll(0, window.scrollY + accTop);
        }
      }
    }
    if (state.sum > state.allComments.length) {
      void getAllComments();
    }
  };

  const {summary} = state;
  if (!summary) {
    return null;
  }
  const average =
    (5 * summary["5"] + 4 * summary["4"] + 3 * summary["3"] + 2 * summary["2"] + summary["1"]) / state.sum;

  const CommentsSummary = () => {
    return <span>({state.sum ? t("{{sum}} Bewertungen", {sum: state.sum}) : t("Laden...")})</span>;
  };

  const RatingStarsRow: React.FC = () => {
    if (!headlineElement) {
      return null;
    }
    const StarsRow = () => (
      <div className={styles.ratingLink}>
        <RatingStars rating={Math.round(average * 2)} size={"medium"} />
        <span onClick={handleRatingsClick}>
          {state.sum ? t("{{sum}} Bewertungen", {sum: state.sum}) : t("Laden...")}
        </span>
      </div>
    );

    return ReactDOM.createPortal(<StarsRow />, headlineElement);
  };

  return (
    <>
      <RatingStarsRow />
      <div ref={headlineDiv} className="accordion__item__headline" onClick={handleToggleClick}>
        <div className={styles.title}>
          <span>{t("Bewertungen")}</span>
          <RatingStars rating={Math.round(average * 2)} size={"medium"} />
          {!isMobile ? <CommentsSummary /> : <span>({state.sum})</span>}
        </div>
      </div>
      <div className="accordion__item__content">
        <div>
          <ShopRatingSummary
            summary={summary}
            filterSet={state.filter.filterSet}
            onFilterChange={(filterSet) => {
              setState({...state, filter: {...state.filter, filterSet}});
            }}
          />
          <div className={styles.legitNote}>
            {t("Alle Bewertungen stammen von verifizierten Käufen")}.&nbsp;
            <a href={t("privacy-link", {ns: "checkout"})} target="_blank" rel="noreferrer">
              {t("Mehr Informationen")}
            </a>
          </div>
          {!isMobile && (
            <RatingFilter
              defaultState={{show: state.filter.show, sort: state.filter.sort}}
              filteredComments={state.filteredComments.length}
              onChange={(filterState) => {
                setState({...state, filter: {...state.filter, ...filterState}});
              }}
            />
          )}

          {isMobile && (
            <Fragment>
              <div
                className={styles.filterIcon}
                onClick={() => {
                  setDrawerOpen(true);
                }}
              >
                {t("Filtern & Sortieren")}
              </div>
              <div>
                {Array.from(state.filter.filterSet).map((item: number) => {
                  return (
                    <CancelFilterButton
                      key={item}
                      onCancel={() => {
                        const updatedSet = state.filter.filterSet;
                        updatedSet.delete(item);
                        setState({...state, filter: {...state.filter, filterSet: updatedSet}});
                      }}
                    >
                      {t("{{item}} Sterne", {item})}
                    </CancelFilterButton>
                  );
                })}
                {state.filter.show === "one" && (
                  <CancelFilterButton
                    onCancel={() => {
                      setState({...state, filter: {...state.filter, show: initialFilterState.show}});
                    }}
                  >
                    {t("Ausgewählte Variante")}
                  </CancelFilterButton>
                )}
              </div>
            </Fragment>
          )}

          <Drawer
            className={styles.drawerWrapper}
            open={drawerOpen}
            onClose={() => {
              setDrawerOpen(false);
            }}
          >
            <RatingFilterMobile
              filterState={state.filter}
              onChange={(filterState) => {
                setState({...state, filter: {...state.filter, ...filterState}});
                setDrawerOpen(false);
              }}
              onReset={() => {
                setState((prevState) => {
                  const updatedSet = state.filter.filterSet;
                  updatedSet.clear();

                  return {...prevState, filter: {...initialFilterState, filterSet: updatedSet}};
                });
                setDrawerOpen(false);
              }}
            />
          </Drawer>
          <ShopRatingComments
            comments={state.itemComments ? state.itemComments : state.allComments}
            filter={state.filter}
            onFilterComments={(comments) => {
              setState({...state, filteredComments: comments});
            }}
          />
        </div>
      </div>
    </>
  );
};

export const CancelFilterButton: React.FC<{onCancel: () => void; className?: string}> = ({
  children,
  className,
  onCancel,
}) => (
  <div className={[styles.cancelFilterButton, className].join(" ")}>
    {children}
    <span className={styles.closeIcon} onClick={onCancel} />
  </div>
);

export default ShopRatingSection;
